import { useEffect, useMemo } from 'react';

import { parseAsStringEnum, useQueryState } from 'nuqs';

import { Sort } from '@uikit/icons/Sort';
import { Dropdown, DropdownItem } from '@uikit/index';

import type {
  RewardPoolsReadableSearchParamsOrderBy} from '@shared/api/rewardPools';
import {
  REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY
} from '@shared/api/rewardPools';

import type { RewardPoolStatus } from '@api/types/rewardPools';
import { REWARD_POOL_STATUS } from '@api/types/rewardPools';

const sortByFiltersOptionsInOrder: {
  value: RewardPoolsReadableSearchParamsOrderBy;
  label: string;
}[] = [
  {
    value: REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY.NEWEST,
    label: 'Newest',
  },
  {
    value: REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY.OLDEST,
    label: 'Oldest',
  },
  {
    value: REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY.ENDING_SOON,
    label: 'Ending Soon',
  },
];

export const getSortByFiltersOptionsOrder = (status: RewardPoolStatus) => {
  return sortByFiltersOptionsInOrder.filter(({ value }) => {
    if (
      status === REWARD_POOL_STATUS.ACTIVE &&
      value === REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY.OLDEST
    ) {
      return false;
    }

    if (
      status !== REWARD_POOL_STATUS.ACTIVE &&
      value === REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY.ENDING_SOON
    ) {
      return false;
    }

    return true;
  });
};

export const DEFAULT_SORT_BY_FILTER_VALUE = REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY.NEWEST;

export const useRewardPoolsOrderByFilterQueryState = () => {
  return useQueryState(
    'order',
    parseAsStringEnum<RewardPoolsReadableSearchParamsOrderBy>(
      Object.values(REWARD_POOLS_READABLE_SEARCH_PARAMS_ORDER_BY),
    )
      .withDefault(DEFAULT_SORT_BY_FILTER_VALUE)
      .withOptions({ clearOnDefault: true }),
  );
};

type OrderByFilterProps = {
  statusFilterValue: RewardPoolStatus;
};

export const OrderByFilter = ({ statusFilterValue }: OrderByFilterProps) => {
  const [filter, setFilter] = useRewardPoolsOrderByFilterQueryState();
  const sortOptions = useMemo(
    () => getSortByFiltersOptionsOrder(statusFilterValue),
    [statusFilterValue],
  );

  const activeOption = sortOptions.find((option) => option.value === filter);
  const defaultOption = sortOptions[0];

  useEffect(() => {
    if (!activeOption?.value) {
      void setFilter(defaultOption.value);
    }
  }, [activeOption?.value, defaultOption.value, setFilter]);

  return (
    <Dropdown
      variant="secondary"
      placement="bottom-end"
      trigger={
        <button className="msq-btn msq-btn-lg whitespace-nowrap msq-btn-secondary">
          <Sort className="msq-btn-icon-child msq-btn-icon-child-left" />
          {activeOption?.label}
        </button>
      }
    >
      {sortOptions.map(({ value, label }) => (
        <DropdownItem
          onClick={() => {
            setFilter(value);
          }}
          variant="secondary"
          key={value}
          isActive={filter === value}
          label={label}
        />
      ))}
    </Dropdown>
  );
};
